// « Montrer l'endroit sur la page » : trouver l'élément signifiant sous le curseur ou au focus, et le nommer en français (DECISIONS §32).
// Aucun contenu de la page n'est copié au-delà d'un libellé court : pas de capture d'écran, pas de texte des cartes voisines.

export interface ElementDesigne {
  libelle: string;
  chemin: string;
  position: { x: number; y: number; w: number; h: number };
}

const BALISES = ['button', 'a', 'input', 'select', 'textarea', 'label', 'h1', 'h2', 'h3', 'th', 'td', 'img'];
const CLASSES = ['card', 'card-title', 'nav-link', 'selectable', 'badge', 'tab', 'chip', 'page-title', 'brand', 'sidebar-label', 'toolbar', 'modal-title'];

/** Un élément dont on peut dire « celui-là » : on remonte du nœud touché jusqu'au premier qui a un sens (bouton, carte, titre…). */
export function signifiant(el: Element): boolean {
  if (BALISES.includes(el.tagName.toLowerCase())) return true;
  const role = el.getAttribute('role');
  if (role && role !== 'presentation' && role !== 'none' && role !== 'grid' && role !== 'tablist') return true;
  return CLASSES.some((c) => el.classList.contains(c)) || el.hasAttribute('data-jour') || el.hasAttribute('data-cle');
}

/** `ignorer` : les éléments de la fenêtre d'avis elle-même ne se désignent pas. */
export function cible(el: Element | null, ignorer: (e: Element) => boolean): Element | null {
  if (!el || ignorer(el) || el === document.body || el === document.documentElement) return null;
  let cur: Element | null = el;
  for (let i = 0; cur && cur !== document.body && i < 7; i++, cur = cur.parentElement) {
    if (ignorer(cur)) return null;
    if (signifiant(cur)) return cur;
  }
  return el;
}

function court(t: string, max: number): string {
  const s = t.replace(/\s+/g, ' ').trim();
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

function texte(el: Element): string {
  const h = el as HTMLElement;
  const direct = el.getAttribute('aria-label') ?? (el as HTMLImageElement).alt ?? el.getAttribute('placeholder') ?? el.getAttribute('title');
  return court(direct || (h.innerText ?? '').split('\n')[0] || '', 60);
}

function genre(el: Element): string {
  const t = el.tagName.toLowerCase(), c = el.classList, role = el.getAttribute('role');
  if (c.contains('nav-link')) return 'Rubrique du menu';
  if (t === 'button' || role === 'button' || c.contains('selectable') || c.contains('chip')) return 'Bouton';
  if (c.contains('tab') || role === 'tab') return 'Onglet';
  if (t === 'a') return 'Lien';
  if (t === 'input' || t === 'select' || t === 'textarea') return 'Champ';
  if (/^h[1-3]$/.test(t) || c.contains('page-title') || c.contains('card-title') || c.contains('modal-title')) return 'Titre';
  if (t === 'th' || t === 'td') return 'Case de tableau';
  if (el.hasAttribute('data-jour')) return 'Jour du calendrier';
  if (c.contains('badge')) return 'Étiquette';
  if (c.contains('card')) return 'Bloc';
  if (c.contains('brand')) return 'Logo et titre';
  return 'Zone';
}

/** Dans quel bloc de la page se trouve l'élément (« Vos remplacements », « Menu de gauche », « Calendrier, jeudi 29 »…). */
function contexte(el: Element): string {
  for (let cur = el.parentElement; cur && cur !== document.body; cur = cur.parentElement) {
    const jour = cur.getAttribute('data-jour');
    if (jour) return `Calendrier, ${jour}`;
    if (cur.getAttribute('role') === 'dialog') {
      const t = cur.querySelector('.modal-title, h2, h3');
      return t ? `Fenêtre « ${texte(t)} »` : 'Fenêtre';
    }
    if (cur.classList.contains('card')) {
      const t = cur.querySelector('.card-title, h2, h3');
      if (t) return texte(t);
    }
    if (cur.classList.contains('sidebar')) return 'Menu de gauche';
    if (cur.classList.contains('topbar')) return 'Barre du haut';
    if (cur.classList.contains('tabs')) return 'Onglets';
  }
  return '';
}

function chemin(el: Element): string {
  const parts: string[] = [];
  for (let cur: Element | null = el, n = 0; cur && cur !== document.body && n < 6; cur = cur.parentElement, n++) {
    if (cur.id) { parts.unshift('#' + cur.id); break; }
    let s = cur.tagName.toLowerCase();
    if (cur.classList.length) s += '.' + cur.classList[0];
    const p = cur.parentElement;
    if (p) {
      const freres = Array.from(p.children).filter((x) => x.tagName === cur!.tagName);
      if (freres.length > 1) s += `:nth(${freres.indexOf(cur) + 1})`;
    }
    parts.unshift(s);
  }
  return parts.join(' > ');
}

export function nommer(el: Element): ElementDesigne {
  const t = texte(el), ctx = contexte(el), r = el.getBoundingClientRect();
  const libelle = genre(el) + (t ? ` « ${t} »` : '') + (ctx && ctx !== t ? ` · ${ctx}` : '');
  return { libelle, chemin: chemin(el), position: { x: Math.round(r.left), y: Math.round(r.top), w: Math.round(r.width), h: Math.round(r.height) } };
}
